import axios from 'axios';
import { ApiResponse, PaginatedResponse } from '../types';

// Alternative API URLs to avoid blocking
const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL || 'https://seniorguideme.com/api';
  }
  
  // Try different localhost alternatives for development
  const alternatives = [
    process.env.REACT_APP_API_URL,
    'http://127.0.0.1:3001/api',
    'http://localhost:3001/api'
  ].filter(Boolean);
  
  return alternatives[0];
};

// Create axios instance
const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Debug logging for placement creation
  if (config.url === '/placements' && config.method === 'post') {
    console.log('🔍 DEBUG: API request interceptor - placement creation');
    console.log('🔍 DEBUG: Request URL:', config.baseURL + config.url);
    console.log('🔍 DEBUG: Request method:', config.method);
    console.log('🔍 DEBUG: Request headers:', config.headers);
    console.log('🔍 DEBUG: Request data:', config.data);
    console.log('🔍 DEBUG: Request data type:', typeof config.data);
    console.log('🔍 DEBUG: Request data stringified:', JSON.stringify(config.data));
  }
  
  // Debug logging for client creation
  if (config.url === '/clients' && config.method === 'post') {
    console.log('🔍 DEBUG: API request interceptor - client creation');
    console.log('🔍 DEBUG: Request URL:', config.baseURL + config.url);
    console.log('🔍 DEBUG: Request method:', config.method);
    console.log('🔍 DEBUG: Request headers:', config.headers);
    console.log('🔍 DEBUG: Request data:', config.data);
    console.log('🔍 DEBUG: Request data type:', typeof config.data);
    console.log('🔍 DEBUG: Request data stringified:', JSON.stringify(config.data));
  }
  
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  
  logout: () => api.post('/auth/logout'),
  
  getProfile: () => api.get('/auth/me'),
  
  getUsers: () => api.get('/auth/users'),
};

// Clients API
export const clientsAPI = {
  getClients: (params?: any) => api.get('/clients', { params }),
  
  getClient: (id: string) => api.get(`/clients/${id}`),
  
  createClient: (clientData: any) => api.post('/clients', clientData),
  
  updateClient: (id: string, clientData: any) =>
    api.put(`/clients/${id}`, clientData),
  
  deleteClient: (id: string) => api.delete(`/clients/${id}`),
  
  getClientsDashboard: () => api.get('/clients/dashboard'),
};

// Facilities API
export const facilitiesAPI = {
  getFacilities: (params?: any) => api.get('/facilities', { params }),
  
  getFacility: (id: string) => api.get(`/facilities/${id}`),
  
  createFacility: (facilityData: any) => api.post('/facilities', facilityData),
  
  updateFacility: (id: string, facilityData: any) =>
    api.put(`/facilities/${id}`, facilityData),
  
  deleteFacility: (id: string) => api.delete(`/facilities/${id}`),
  
  getFacilitiesDashboard: () => api.get('/facilities/dashboard'),
};

// Placements API
export const placementsAPI = {
  getPlacements: (params?: any) => api.get('/placements', { params }),
  
  getPlacement: (id: string) => api.get(`/placements/${id}`),
  
  createPlacement: (placementData: any) => api.post('/placements', placementData),
  
  updatePlacement: (id: string, placementData: any) =>
    api.put(`/placements/${id}`, placementData),
  
  deletePlacement: (id: string) => api.delete(`/placements/${id}`),
  
  getPlacementsDashboard: () => api.get('/placements/dashboard'),
  
  getSwimLanes: () => api.get('/tasks/swim-lanes'),
  
  updateSwimLaneStage: (id: string, stage: string) =>
    api.put(`/placements/${id}/swim-lane`, { swimLaneStage: stage }),
};

// Tasks API
export const tasksAPI = {
  getTasks: (params?: any) => api.get('/tasks', { params }),
  
  getTask: (id: string) => api.get(`/tasks/${id}`),
  
  createTask: (taskData: any) => api.post('/tasks', taskData),
  
  updateTask: (id: string, taskData: any) => api.put(`/tasks/${id}`, taskData),
  
  deleteTask: (id: string) => api.delete(`/tasks/${id}`),
  
  getTasksDashboard: () => api.get('/tasks/dashboard'),
  
  getSwimLanes: () => api.get('/tasks/swim-lanes'),
  
  addTaskNote: (id: string, note: string) =>
    api.post(`/tasks/${id}/notes`, { note }),
  
  updateTaskChecklist: (id: string, checklist: any[]) =>
    api.put(`/tasks/${id}/checklist`, { checklist }),
  
  updateTaskStatus: (id: string, status: string) =>
    api.patch(`/tasks/${id}/status`, { status }),
    
  // Enhanced Phase 7 APIs
  addCommunication: (id: string, communication: { type: string; content: string }) =>
    api.post(`/tasks/${id}/communications`, communication),
    
  updateProgress: (id: string, progress: { percentage?: number; milestones?: any[] }) =>
    api.patch(`/tasks/${id}/progress`, progress),
    
  getSmartMatches: (id: string) => api.get(`/tasks/${id}/smart-matches`),
  
  getAnalytics: (timeframe?: string) => api.get('/tasks/analytics', { params: { timeframe } }),
};

// Resources API
export const resourcesAPI = {
  getResources: (params?: any) => api.get('/resources', { params }),
  
  getResource: (id: string) => api.get(`/resources/${id}`),
  
  uploadResource: (formData: FormData) =>
    api.post('/resources', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  
  updateResource: (id: string, resourceData: any) =>
    api.put(`/resources/${id}`, resourceData),
  
  deleteResource: (id: string) => api.delete(`/resources/${id}`),
  
  downloadResource: (id: string) => 
    api.get(`/resources/${id}/download`, { 
      responseType: 'blob' 
    }),
  
  getResourceInfo: (id: string) => api.get(`/resources/${id}/info`),
  
  getResourcesByObject: (objectType: string, objectId: string) =>
    api.get(`/resources/by-object/${objectType}/${objectId}`),
  
  getResourcesDashboard: () => api.get('/resources/dashboard'),
};

export default api;
